import { createFileRoute } from "@tanstack/react-router"

import { useNavigate } from "@tanstack/react-router"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@fieldbook/ui/sheet"
import { useAuth } from "@/hooks/useAuth"
import { ChevronDown, InfoIcon } from "lucide-react"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@fieldbook/ui/collapsible"
import { Switch } from "@fieldbook/ui/switch"
import { Label } from "@fieldbook/ui/label"
import { Checkbox } from "@fieldbook/ui/checkbox"
import { IconPopover } from "@fieldbook/ui/popover"
import { useSettings } from "@/contexts/settings"
import { GeoLocationInfoPanel } from "./-components/GeolocationPermissions"
import { LogoutButton } from "./-components/LogoutButton"
import {
  ResetLocalDataDescription,
  type ResetPhase,
} from "./-components/ResetLocalDataDescription"
import { describePendingUploads } from "./-components/resetLocalDataCopy"
import { useCallback, useEffect, useState } from "react"
import { useSwStatus } from "@/contexts/swStatus"
import { Button } from "@fieldbook/ui/button"
import { cn } from "@fieldbook/ui/utils"
import {
  clearFilesystemHttpCache,
  mediaCache,
  getFilesystemHttpCacheStats,
  appShell,
} from "@/platform"
import type { FilesystemHttpCacheStats, MediaCacheStats } from "@/platform"
import { OFFLINE_CACHE_POLICIES } from "@/lib/offline/cachePolicies"
import { clearAllCachedAreas } from "@/lib/offline/mapTileAreaStore"
import {
  drainPendingUploads,
  getLocalDataCounts,
  hasDeviceOnlyData,
  hasPendingUploads,
  resetDeletesData,
  resetLocalData,
  type LocalDataCounts,
} from "@/lib/powersync/resetLocalData"
import * as Sentry from "@sentry/react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@fieldbook/ui/alert-dialog"

export const Route = createFileRoute("/_private/settings/")({
  component: SettingsSheet,
})

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** How long to wait for pending uploads to flush before offering a
 * destructive reset. */
const RESET_DRAIN_TIMEOUT_MS = 30_000
const RESET_DRAIN_POLL_MS = 1000

function SettingsSheet() {
  const navigate = useNavigate()
  const { logout } = useAuth()

  const handleLogout = async () => {
    await logout.mutateAsync()
  }

  const { updateSettings, settings } = useSettings()
  const supportsPwaUpdates = appShell.supportsPwaUpdates()
  const handleAllowTrackingChange = (checked: boolean) => {
    updateSettings({ location: { allowTracking: checked } })
  }

  const handleSetDarkMode = (checked: boolean) => {
    updateSettings({ theme: checked ? "dark" : "light" })
  }

  const [isCheckingUpdate, setIsCheckingUpdate] = useState(false)
  const [updateCheckResult, setUpdateCheckResult] = useState<
    "none" | "checked" | "error"
  >("none")
  const {
    checkForUpdate,
    updateAvailable,
    updateServiceWorker,
    ignoreUpdate,
    isDownloading,
    isUpdating,
  } = useSwStatus()

  const canInstallUpdate = updateAvailable && ignoreUpdate

  const handleCheckForUpdate = useCallback(async () => {
    if (canInstallUpdate) {
      await updateServiceWorker()
      return
    }
    setIsCheckingUpdate(true)
    setUpdateCheckResult("none")
    try {
      await checkForUpdate()
      setUpdateCheckResult("checked")
    } catch (error) {
      console.error("Failed to check for updates:", error)
      setUpdateCheckResult("error")
    } finally {
      setIsCheckingUpdate(false)
    }
  }, [checkForUpdate, canInstallUpdate, updateServiceWorker])

  const [mediaCacheStats, setMediaCacheStats] =
    useState<MediaCacheStats | null>(null)
  const [isClearingMediaCache, setIsClearingMediaCache] = useState(false)
  const [mapTileCacheStats, setMapTileCacheStats] =
    useState<FilesystemHttpCacheStats | null>(null)
  const [isClearingMapTileCache, setIsClearingMapTileCache] = useState(false)
  const [speciesImageCacheStats, setSpeciesImageCacheStats] =
    useState<FilesystemHttpCacheStats | null>(null)
  const [isClearingSpeciesImageCache, setIsClearingSpeciesImageCache] =
    useState(false)
  const [isStorageOpen, setIsStorageOpen] = useState(false)

  const refreshMediaCacheStats = useCallback(async () => {
    setMediaCacheStats(await mediaCache.getStats())
  }, [])

  const refreshMapTileCacheStats = useCallback(async () => {
    setMapTileCacheStats(
      await getFilesystemHttpCacheStats(
        OFFLINE_CACHE_POLICIES.mapboxTiles.cacheName,
      ),
    )
  }, [])

  const refreshSpeciesImageCacheStats = useCallback(async () => {
    setSpeciesImageCacheStats(
      await getFilesystemHttpCacheStats(
        OFFLINE_CACHE_POLICIES.alaRemoteImages.cacheName,
      ),
    )
  }, [])

  useEffect(() => {
    void refreshMediaCacheStats()
    void refreshMapTileCacheStats()
    void refreshSpeciesImageCacheStats()
  }, [
    refreshMapTileCacheStats,
    refreshMediaCacheStats,
    refreshSpeciesImageCacheStats,
  ])

  const handleClearMediaCache = useCallback(async () => {
    setIsClearingMediaCache(true)
    try {
      await mediaCache.clear()
      await refreshMediaCacheStats()
    } finally {
      setIsClearingMediaCache(false)
    }
  }, [refreshMediaCacheStats])

  const handleClearMapTileCache = useCallback(async () => {
    setIsClearingMapTileCache(true)
    try {
      await clearFilesystemHttpCache(
        OFFLINE_CACHE_POLICIES.mapboxTiles.cacheName,
      )
      clearAllCachedAreas()
      await refreshMapTileCacheStats()
    } finally {
      setIsClearingMapTileCache(false)
    }
  }, [refreshMapTileCacheStats])

  const handleClearSpeciesImageCache = useCallback(async () => {
    setIsClearingSpeciesImageCache(true)
    try {
      await clearFilesystemHttpCache(
        OFFLINE_CACHE_POLICIES.alaRemoteImages.cacheName,
      )
      await refreshSpeciesImageCacheStats()
    } finally {
      setIsClearingSpeciesImageCache(false)
    }
  }, [refreshSpeciesImageCacheStats])

  const [localDataCounts, setLocalDataCounts] =
    useState<LocalDataCounts | null>(null)
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false)
  const [resetPhase, setResetPhase] = useState<ResetPhase>("idle")
  const [resetError, setResetError] = useState<string | null>(null)
  const [includeDeviceOnly, setIncludeDeviceOnly] = useState(false)
  const isResetBusy = resetPhase === "draining" || resetPhase === "resetting"
  // Only honour the opt-in while the dialog is showing something to delete.
  const deleteDeviceOnly =
    includeDeviceOnly &&
    localDataCounts !== null &&
    hasDeviceOnlyData(localDataCounts)

  const refreshLocalDataCounts = useCallback(async () => {
    try {
      setLocalDataCounts(await getLocalDataCounts())
    } catch {
      setLocalDataCounts(null)
    }
  }, [])

  useEffect(() => {
    void refreshLocalDataCounts()
  }, [refreshLocalDataCounts])

  const performReset = useCallback(
    async (counts: LocalDataCounts | null) => {
      // Support visibility: the user explicitly chose to lose data that
      // hasn't reached the server.
      if (resetDeletesData(counts, { includeDeviceOnly: deleteDeviceOnly })) {
        Sentry.captureMessage("Reset local data deleted unsynced data", {
          level: "warning",
          tags: { source: "reset-local-data" },
          extra: { counts, includeDeviceOnly: deleteDeviceOnly },
        })
      }
      setResetPhase("resetting")
      try {
        await resetLocalData({ includeDeviceOnly: deleteDeviceOnly })
        window.location.reload()
      } catch (error) {
        setResetPhase("idle")
        setIsResetDialogOpen(false)
        setResetError(
          error instanceof Error ? error.message : "Failed to reset local data",
        )
      }
    },
    [deleteDeviceOnly],
  )

  const handleConfirmReset = useCallback(async () => {
    setResetError(null)
    let counts = localDataCounts
    if (counts === null || hasPendingUploads(counts)) {
      setResetPhase("draining")
      try {
        counts = await drainPendingUploads({
          timeoutMs: RESET_DRAIN_TIMEOUT_MS,
          pollMs: RESET_DRAIN_POLL_MS,
          onProgress: setLocalDataCounts,
        })
        if (hasPendingUploads(counts)) {
          setResetPhase("drain-failed")
          return
        }
      } catch {
        // The counts shown may be stale; say so rather than understate.
        setLocalDataCounts(null)
        setResetPhase("drain-failed")
        return
      }
    }
    await performReset(counts)
  }, [localDataCounts, performReset])

  const handleOpenResetDialog = useCallback(() => {
    setResetPhase("idle")
    setResetError(null)
    setIncludeDeviceOnly(false)
    setIsResetDialogOpen(true)
    void refreshLocalDataCounts()
  }, [refreshLocalDataCounts])

  return (
    <Sheet
      open={true}
      onOpenChange={(open) => {
        if (!open) {
          navigate({ to: "/projects", replace: true })
        }
      }}
    >
      {/* Width is capped rather than fixed. A hardcoded 390px — an iPhone's
          viewport — is wider than the 360px many Android devices report, and
          the sheet is positioned fixed, so the overhang fell off screen. */}
      <SheetContent
        side="left"
        className="w-full max-w-[390px] p-2 pr-4 sm:max-w-[540px]"
      >
        <SheetHeader className="p-0">
          <SheetTitle className="text-xl font-medium">Settings</SheetTitle>
        </SheetHeader>
        <div className="h-full">
          <div className="flex h-full flex-col justify-between gap-2">
            <div className="flex flex-col pt-2">
              <span className="text-muted-foreground text-lg">General</span>
              <div className="flex items-center justify-between border-y py-4">
                <span className="flex items-center gap-2">
                  <Label htmlFor="theme">Dark Mode</Label>
                </span>
                <Switch
                  id="theme"
                  onCheckedChange={handleSetDarkMode}
                  checked={settings.theme === "dark"}
                />
              </div>
              {supportsPwaUpdates && (
                <div className="flex items-center justify-between border-y py-4">
                  <span className="flex flex-col gap-1">
                    <Label>Updates</Label>
                    {isDownloading && (
                      <span className="text-muted-foreground text-xs">
                        Checking for update…
                      </span>
                    )}
                    {canInstallUpdate && (
                      <span className="text-xs text-green-600">
                        Update ready to install
                      </span>
                    )}
                    {updateCheckResult === "checked" && !updateAvailable && (
                      <span className="text-muted-foreground text-xs">
                        You're up to date
                      </span>
                    )}
                    {updateCheckResult === "error" && (
                      <span className="text-destructive text-xs">
                        Failed to check for updates
                      </span>
                    )}
                  </span>
                  <Button
                    size={"sm"}
                    onClick={handleCheckForUpdate}
                    disabled={isCheckingUpdate || isDownloading || isUpdating}
                  >
                    {isDownloading
                      ? "Checking…"
                      : isUpdating
                        ? "Updating…"
                        : canInstallUpdate
                          ? "Update Now"
                          : isCheckingUpdate
                            ? "Checking…"
                            : "Check Now"}
                  </Button>
                </div>
              )}

              <Collapsible open={isStorageOpen} onOpenChange={setIsStorageOpen}>
                <CollapsibleTrigger className="flex w-full items-center justify-between py-2">
                  <span className="text-muted-foreground text-lg">Storage</span>
                  <ChevronDown
                    className={cn(
                      "text-muted-foreground size-4 transition-transform",
                      isStorageOpen && "rotate-180",
                    )}
                  />
                </CollapsibleTrigger>
                <CollapsibleContent className="flex flex-col">
                  <div className="flex items-center justify-between border-y py-4">
                    <span className="flex flex-col gap-1">
                      <Label>Cached media</Label>
                      <span className="text-muted-foreground text-xs">
                        {mediaCacheStats
                          ? `${mediaCacheStats.entryCount} files, ${formatBytes(mediaCacheStats.byteSize)}`
                          : "Calculating…"}
                      </span>
                      {mediaCacheStats && (
                        <span className="text-muted-foreground text-xs">
                          Session: {mediaCacheStats.hits} hits,{" "}
                          {mediaCacheStats.misses} misses,{" "}
                          {mediaCacheStats.writes} writes,{" "}
                          {mediaCacheStats.failures} failures
                        </span>
                      )}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleClearMediaCache}
                      disabled={
                        isClearingMediaCache ||
                        !mediaCacheStats ||
                        mediaCacheStats.entryCount === 0
                      }
                    >
                      {isClearingMediaCache ? "Clearing…" : "Clear"}
                    </Button>
                  </div>
                  <div className="flex items-center justify-between border-y py-4">
                    <span className="flex flex-col gap-1">
                      <Label>Cached map tiles</Label>
                      <span className="text-muted-foreground text-xs">
                        {mapTileCacheStats
                          ? `${mapTileCacheStats.entryCount} files, ${formatBytes(mapTileCacheStats.byteSize)}`
                          : "Calculating…"}
                      </span>
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleClearMapTileCache}
                      disabled={
                        isClearingMapTileCache ||
                        !mapTileCacheStats ||
                        mapTileCacheStats.entryCount === 0
                      }
                    >
                      {isClearingMapTileCache ? "Clearing…" : "Clear"}
                    </Button>
                  </div>
                  <div className="flex items-center justify-between border-y py-4">
                    <span className="flex flex-col gap-1">
                      <Label>Cached species images</Label>
                      <span className="text-muted-foreground text-xs">
                        {speciesImageCacheStats
                          ? `${speciesImageCacheStats.entryCount} files, ${formatBytes(speciesImageCacheStats.byteSize)}`
                          : "Calculating…"}
                      </span>
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleClearSpeciesImageCache}
                      disabled={
                        isClearingSpeciesImageCache ||
                        !speciesImageCacheStats ||
                        speciesImageCacheStats.entryCount === 0
                      }
                    >
                      {isClearingSpeciesImageCache ? "Clearing…" : "Clear"}
                    </Button>
                  </div>
                </CollapsibleContent>
              </Collapsible>

              <span className="text-muted-foreground text-lg">Sync</span>
              <div className="flex items-center justify-between border-y py-4">
                <span className="flex flex-col gap-1">
                  <span className="flex items-center gap-2">
                    <Label>Project data</Label>
                    <IconPopover
                      label="About re-downloading project data"
                      icon={<InfoIcon className="size-4" />}
                    >
                      <p>
                        Clears project data on this device and re-downloads it
                        from the server. Use this if project data is stuck
                        syncing or looks incomplete. Photos waiting to upload,
                        records saved on this device and sync failures are kept
                        unless you choose to delete them.
                      </p>
                    </IconPopover>
                  </span>
                  <span className="text-muted-foreground text-xs">
                    {localDataCounts === null
                      ? "Checking for unsynced changes…"
                      : hasPendingUploads(localDataCounts)
                        ? `${describePendingUploads(localDataCounts)} on this device`
                        : "All changes synced"}
                  </span>
                  {resetError && (
                    <span className="text-destructive text-xs">
                      {resetError}
                    </span>
                  )}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-destructive hover:text-destructive"
                  onClick={handleOpenResetDialog}
                  disabled={isResetBusy}
                >
                  Re-download
                </Button>
              </div>
              <AlertDialog
                open={isResetDialogOpen}
                onOpenChange={(open) => {
                  if (!isResetBusy) setIsResetDialogOpen(open)
                }}
              >
                <AlertDialogContent className="space-y-2">
                  <AlertDialogHeader className="text-left">
                    <AlertDialogTitle>
                      Re-download project data?
                    </AlertDialogTitle>
                    <AlertDialogDescription asChild>
                      <div className="space-y-2">
                        <ResetLocalDataDescription
                          phase={resetPhase}
                          counts={localDataCounts}
                          includeDeviceOnly={deleteDeviceOnly}
                        />
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  {localDataCounts &&
                    hasDeviceOnlyData(localDataCounts) &&
                    (resetPhase === "idle" ||
                      resetPhase === "drain-failed") && (
                      <div className="flex items-start gap-2">
                        <Checkbox
                          id="reset-include-device-only"
                          className="mt-0.5"
                          checked={includeDeviceOnly}
                          onCheckedChange={(checked) =>
                            setIncludeDeviceOnly(checked === true)
                          }
                        />
                        <Label
                          htmlFor="reset-include-device-only"
                          className="text-sm font-normal leading-snug"
                        >
                          Also delete data that only exists on this device
                        </Label>
                      </div>
                    )}
                  <AlertDialogFooter className="flex-row justify-end space-x-2">
                    <AlertDialogCancel className="mt-0" disabled={isResetBusy}>
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-destructive hover:bg-destructive/90 text-white"
                      disabled={isResetBusy}
                      onClick={(e) => {
                        e.preventDefault()
                        if (resetPhase === "drain-failed") {
                          void performReset(localDataCounts)
                        } else {
                          void handleConfirmReset()
                        }
                      }}
                    >
                      {resetPhase === "draining"
                        ? "Uploading…"
                        : resetPhase === "resetting"
                          ? "Reloading…"
                          : resetPhase === "drain-failed"
                            ? resetDeletesData(localDataCounts, {
                                includeDeviceOnly: deleteDeviceOnly,
                              })
                              ? "Delete and re-download"
                              : "Re-download anyway"
                            : deleteDeviceOnly
                              ? "Delete and re-download"
                              : "Re-download"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <span className="text-muted-foreground text-lg">Location</span>

              <div className="flex items-center justify-between border-y py-4">
                <span className="flex items-center gap-2">
                  <Label htmlFor="allow-tracking">
                    Allow Location Tracking
                  </Label>
                  <IconPopover
                    label="About location tracking"
                    icon={<InfoIcon className="size-4" />}
                  >
                    <p>
                      Allow Fieldbook to track your location for safety
                      purposes. This information is only available to you and
                      your organisation, and is only active when the app is
                      open.
                    </p>
                  </IconPopover>
                </span>
                <Switch
                  id="allow-tracking"
                  onCheckedChange={handleAllowTrackingChange}
                  checked={settings.location.allowTracking}
                />
              </div>
              <div className="flex flex-col border-y py-2">
                <span className="text-muted-foreground">
                  Device Location Permissions
                </span>
                <GeoLocationInfoPanel />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between border-y py-4">
                <span className="text-muted-foreground">Fieldbook Version</span>
                <span className="text-muted-foreground font-mono">
                  {__APP_VERSION__}
                  <span className="ml-2 opacity-60">({__BUILD_ID__})</span>
                </span>
              </div>
              <div className="flex items-center justify-end border-y py-4">
                <span className="text-muted-foreground">
                  <a
                    href={`${import.meta.env.VITE_WEB_APP_URL}/privacy`}
                    target="_blank"
                  >
                    Privacy Policy
                  </a>
                </span>
              </div>
              <div className="flex flex-col gap-1 border-t">
                <LogoutButton onLogout={handleLogout} />
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
